import { Accordion, Button, Stack, Text as MantineText } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons';
import { FC, useMemo, useState } from 'react';
import { Contact } from '../../../Service/ContactS';
import { AgendaItem } from '../../../Types/AgendaItem';
import { TaskField } from './TaskField';

type Props = {
  contact: Contact;
  tasks: AgendaItem[];
  onTaskChange: (task: AgendaItem) => void;
};

const ParticipantTaskGroup: FC<Props> = ({ contact, tasks, onTaskChange }) => {
  const [showCompleted, setShowCompleted] = useState(false);

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((firstTask, secondTask) => {
        const firstCompletedOrder = firstTask.completed ? 1 : 0;
        const secondCompletedOrder = secondTask.completed ? 1 : 0;

        if (firstCompletedOrder !== secondCompletedOrder) {
          return firstCompletedOrder - secondCompletedOrder;
        }

        const firstDueDate = firstTask.dueDate ? new Date(firstTask.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        const secondDueDate = secondTask.dueDate ? new Date(secondTask.dueDate).getTime() : Number.MAX_SAFE_INTEGER;

        if (firstDueDate !== secondDueDate) {
          return firstDueDate - secondDueDate;
        }

        return firstTask.name.localeCompare(secondTask.name);
      }),
    [tasks],
  );

  const completedTasksCount = sortedTasks.filter((task) => task.completed).length;
  const visibleTasks = showCompleted ? sortedTasks : sortedTasks.filter((task) => !task.completed);

  if (tasks.length === 0) return null;

  return (
    <Accordion
      sx={{ flexGrow: 1 }}
      chevron={<IconChevronRight size="1rem" />}
      styles={{
        chevron: {
          '&[data-rotate]': {
            transform: 'rotate(90deg)',
          },
        },
      }}
      variant="separated"
      defaultValue="tasks"
      chevronPosition="left"
    >
      <Accordion.Item value="tasks">
        <Accordion.Control>{contact.personDescription}</Accordion.Control>
        <Accordion.Panel>
          <Stack>
            {visibleTasks.map((task) => (
              <TaskField key={task.id} task={task} entityId={task.entityId} onChange={onTaskChange} />
            ))}
            {!showCompleted && completedTasksCount > 0 && (
              <Button
                variant="subtle"
                size="xs"
                compact
                onClick={() => setShowCompleted(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {completedTasksCount} bereits erledigte Aufgaben einblenden
              </Button>
            )}
            {showCompleted && completedTasksCount > 0 && (
              <MantineText size="xs" color="dimmed" sx={{ cursor: 'pointer' }} onClick={() => setShowCompleted(false)}>
                Erledigte Aufgaben ausblenden
              </MantineText>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default ParticipantTaskGroup;
